import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { DesktopInvoice } from 'src/app/services/invoices.service';
import {
  DesktopLicenseDetails,
  LicensesService,
  TransferLicensesResponse,
} from 'src/app/services/licenses.service';
import {
  DesktopLicenseRecords,
  MachinesService,
  RegistrationRecord,
} from 'src/app/services/machines.service';
import { ToastService } from 'src/app/services/toast.service';
import { ErrorService } from 'src/app/services/error.service';

type Sort = {
  field: string;
  direction: 'ASC' | 'DESC';
};

@Component({
  selector: 'app-licenses-table',
  templateUrl: './licenses-table.component.html',
  styleUrl: './licenses-table.component.scss',
})
export class LicensesTableComponent implements AfterViewInit, OnDestroy {
  @Input('licenses') licenses?: DesktopLicenseDetails[];
  @Input('current-sort') currentSort?: Sort;
  @Output('sort-change') sortChange = new EventEmitter<string>();
  @Output('license-transferred') licenseTransferred =
    new EventEmitter<DesktopLicenseDetails>();

  @ViewChildren('noteRow') noteRows!: QueryList<ElementRef<HTMLElement>>;
  @ViewChild('transferDialog') transferDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('regenerateKeyDialog')
  regenerateKeyDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('newUniqueIdInput')
  newUniqueIdInput!: ElementRef<HTMLInputElement>;

  shownKeys = new Set<string>();
  hiddenKeyString = [9, 10, 10, 3, 7]
    .map((length) => '&bull;'.repeat(length))
    .join('-');

  expandedNoteIds = new Set<string>();
  notesWithOverflow = new Set<string>();

  transferingLicense = false;
  errorTransferingLicense?: string = undefined;

  licenseToTransfer: DesktopLicenseDetails | null = null;
  newUniqueId = '';
  isTransfer = true;
  syncRegistrationDatabase = true;

  licenseToRegenerate: DesktopLicenseDetails | null = null;
  regeneratingKey = false;
  errorRegeneratingKey?: string;

  /** Active licenses sharing Current Unique ID with license being transferred */
  sameUidLicensesList: DesktopLicenseDetails[] = [];
  sameUidSelected = new Set<string>();

  existingMachines?: DesktopLicenseRecords = undefined;
  /** Active registration rows only; unique per trimmed Unique ID, newest lastused wins. */
  distinctActiveMachinesList: RegistrationRecord[] = [];
  loadingMachines = false;
  errorLoadingMachines?: string = undefined;
  selectedMachineId?: string = undefined;

  private noteMeasuresSub?: Subscription;

  constructor(
    private errorService: ErrorService,
    private licensesService: LicensesService,
    private machinesService: MachinesService,
    private router: Router,
    private toastService: ToastService
  ) {}

  /**
   * One row per Unique ID from active desktop registration records only.
   * Duplicate Unique IDs: keep row with latest lastused
   */
  private distinctActiveByUniqueId(
    active: RegistrationRecord[]
  ): RegistrationRecord[] {
    const lastUsedMs = (r: RegistrationRecord): number => {
      if (!r.lastused) {
        return 0;
      }
      const t = new Date(r.lastused).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const pickBetter = (
      a: RegistrationRecord,
      b: RegistrationRecord
    ): RegistrationRecord => {
      const ta = lastUsedMs(a);
      const tb = lastUsedMs(b);
      if (tb !== ta) {
        return tb > ta ? b : a;
      }
      return a.registration_id >= b.registration_id ? a : b;
    };

    const byUid = new Map<string, RegistrationRecord>();
    for (const rec of active) {
      const uid = rec.uniqueid?.trim();
      if (!uid) {
        continue;
      }
      const prev = byUid.get(uid);
      byUid.set(uid, prev ? pickBetter(prev, rec) : rec);
    }

    return [...byUid.values()].sort((a, b) => lastUsedMs(b) - lastUsedMs(a));
  }

  ngAfterViewInit(): void {
    this.noteMeasuresSub = this.noteRows.changes.subscribe(() =>
      this.queueNoteMeasurement()
    );
    this.queueNoteMeasurement();
  }

  ngOnDestroy(): void {
    this.noteMeasuresSub?.unsubscribe();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.queueNoteMeasurement();
  }

  hasNotes(license: DesktopLicenseDetails): boolean {
    if (!license.notes) {
      return false;
    }
    return license.notes.trim() !== '';
  }

  isNoteExpanded(licenseId: string): boolean {
    return this.expandedNoteIds.has(licenseId);
  }

  showNoteToggle(licenseId: string): boolean {
    return this.notesWithOverflow.has(licenseId);
  }

  toggleNoteExpand(licenseId: string, event: Event): void {
    event.stopPropagation();
    const nextExpanded = new Set(this.expandedNoteIds);
    if (nextExpanded.has(licenseId)) {
      nextExpanded.delete(licenseId);
    } else {
      nextExpanded.add(licenseId);
    }
    this.expandedNoteIds = nextExpanded;
    this.queueNoteMeasurement();
  }

  private queueNoteMeasurement(): void {
    requestAnimationFrame(() => {
      this.measureNoteOverflows();
    });
  }

  private measureNoteOverflows(): void {
    const notesWithOverflow = new Set<string>();

    for (const noteRow of this.noteRows) {
      const el = noteRow.nativeElement;
      const id = el.dataset['licenseId'];
      if (!id) {
        continue;
      }

      // If the note is expanded, we don't need to measure it
      if (this.isNoteExpanded(id)) {
        notesWithOverflow.add(id);
        continue;
      }

      if (this.noteTextOverflows(el)) {
        notesWithOverflow.add(id);
      }
    }

    this.notesWithOverflow = notesWithOverflow;
  }

  private noteTextOverflows(el: HTMLElement): boolean {
    const width = el.getBoundingClientRect().width;
    if (width <= 0) {
      return false;
    }

    const clone = el.cloneNode(true) as HTMLElement;
    clone.classList.remove('license-notes-clamped');
    clone.style.width = `${width}px`;
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.display = 'block';
    clone.style.visibility = 'hidden';
    clone.style.setProperty('-webkit-line-clamp', 'unset');
    clone.style.setProperty('line-clamp', 'unset');

    document.body.appendChild(clone);
    const naturalHeight = clone.scrollHeight;
    document.body.removeChild(clone);

    return naturalHeight > el.clientHeight + 1;
  }

  latestInvoice(license: DesktopLicenseDetails): DesktopInvoice | null {
    if (!license.invoices || license.invoices.length === 0) {
      return null;
    }

    return license.invoices.reduce((prev, curr) => {
      const prevCreated = new Date(prev.created_at);
      const currCreated = new Date(curr.created_at);
      return prevCreated > currCreated ? prev : curr;
    }, license.invoices[0]);
  }

  getLicenseType(license: DesktopLicenseDetails) {
    return this.licensesService.getType(license);
  }

  getLicensePrice(license: DesktopLicenseDetails) {
    return this.licensesService.getPrice(license);
  }

  browseToInvoice(invoice: DesktopInvoice) {
    this.router.navigate([
      'admin',
      'tenant',
      invoice.tenant_id,
      'invoice',
      invoice.id,
    ]);
  }

  licenseCurrentUniqueId(license: DesktopLicenseDetails): string | null {
    const uid =
      license.activation_key?.unique_id ??
      (license.unique_id != null ? String(license.unique_id) : undefined);
    if (!uid || uid.trim() === '') {
      return null;
    }
    return uid.trim();
  }

  copyActivationKey(license: DesktopLicenseDetails, event?: Event) {
    event?.stopPropagation();
    const key = license.activation_key?.activation_key;
    if (!key) {
      return;
    }
    navigator.clipboard.writeText(key).then(() => {
      this.toastService.addToast('info', 'Activation key copied to clipboard');
    });
  }

  private getSameUidLicenses(
    license: DesktopLicenseDetails
  ): DesktopLicenseDetails[] {
    const uid = this.licenseCurrentUniqueId(license);
    if (!uid || !this.licenses) {
      return [];
    }
    return this.licenses.filter(
      (l) => l.active && this.licenseCurrentUniqueId(l) === uid
    );
  }

  setSameUidSelected(licenseId: string, checked: boolean): void {
    if (checked) {
      this.sameUidSelected.add(licenseId);
    } else {
      this.sameUidSelected.delete(licenseId);
    }
  }

  selectRegistrationForTransfer(machine: RegistrationRecord): void {
    this.selectedMachineId = String(machine.registration_id);
    this.newUniqueId = '';
  }

  isRegistrationSelected(machine: RegistrationRecord): boolean {
    return this.selectedMachineId === String(machine.registration_id);
  }

  /** License IDs included in API call (checkboxes). Fallback when no UID peers list. */
  licenseIdsToTransfer(): string[] {
    if (this.sameUidLicensesList.length === 0 && this.licenseToTransfer) {
      return [this.licenseToTransfer.id];
    }
    return this.sameUidLicensesList
      .filter((l) => this.sameUidSelected.has(l.id))
      .map((l) => l.id);
  }

  showTransferDialog(license: DesktopLicenseDetails) {
    console.log('showTransferDialog - desktop license id: ', license.id);

    this.resetTransferDialog();

    this.licenseToTransfer = license;
    this.sameUidLicensesList = this.getSameUidLicenses(license);
    this.sameUidSelected.clear();
    for (const p of this.sameUidLicensesList) {
      this.sameUidSelected.add(p.id);
    }

    this.getMachinesForTenant(license.tenant_id);
    this.isTransfer = !!license.activation_key;

    this.transferDialog.nativeElement.showModal();
    setTimeout(() => {
      this.newUniqueIdInput.nativeElement.focus();
    }, 100);
  }

  closeTransferDialog() {
    this.transferDialog.nativeElement.close();
    this.resetTransferDialog();
    this.licenseToTransfer = null;
  }

  resetTransferDialog() {
    this.newUniqueId = '';
    this.isTransfer = true;
    this.syncRegistrationDatabase = true;
    this.selectedMachineId = undefined;
    this.existingMachines = undefined;
    this.distinctActiveMachinesList = [];
    this.loadingMachines = false;
    this.errorLoadingMachines = undefined;
    this.sameUidLicensesList = [];
    this.sameUidSelected.clear();
  }

  getMachinesForTenant(tenantId: string) {
    this.loadingMachines = true;
    this.errorLoadingMachines = undefined;

    this.machinesService.getMachinesForTenant(tenantId).subscribe({
      next: (response) => {
        console.log('response', response);
        this.existingMachines = response;
        this.distinctActiveMachinesList = this.distinctActiveByUniqueId(
          response.active ?? []
        );
        this.loadingMachines = false;
        this.errorLoadingMachines = undefined;
      },
      error: (error) => {
        console.error('error', error);
        this.existingMachines = undefined;
        this.distinctActiveMachinesList = [];
        this.loadingMachines = false;
        this.errorLoadingMachines = error.message;
      },
    });
  }

  transferLicense() {
    if (!this.licenseToTransfer) {
      console.error('no license to transfer');
      return;
    }

    this.transferingLicense = true;

    let newUniqueId;
    if (this.newUniqueId && this.newUniqueId.trim() !== '') {
      newUniqueId = this.newUniqueId.trim();
    } else if (this.selectedMachineId) {
      const machine = this.distinctActiveMachinesList.find(
        (m) => String(m.registration_id) === String(this.selectedMachineId)
      );
      newUniqueId = machine?.uniqueid?.trim();
      if (!newUniqueId) {
        this.transferingLicense = false;
        this.errorTransferingLicense =
          'Could not resolve Unique ID for selected machine.';
        return;
      }
    } else {
      console.error('no new unique id or selected machine id');
      this.transferingLicense = false;
      return;
    }

    const ids = this.licenseIdsToTransfer();
    if (ids.length === 0) {
      this.transferingLicense = false;
      this.errorTransferingLicense = 'Select at least one license to transfer.';
      return;
    }

    this.licensesService
      .transferLicenses(
        ids,
        newUniqueId,
        this.isTransfer,
        this.syncRegistrationDatabase
      )
      .subscribe({
        next: (response) => this.handleLicensesTransferred(response),
        error: (error) => this.handleLicenseTransferError(error),
      });
  }

  handleLicensesTransferred(response: TransferLicensesResponse) {
    this.transferingLicense = false;
    this.errorTransferingLicense = undefined;

    const transferredIds = this.licenseIdsToTransfer();

    const byLicenseId = new Map(
      response.activation_keys.map((k) => [k.desktop_license_id, k])
    );

    for (const id of transferredIds) {
      const lic =
        this.sameUidLicensesList.find((l) => l.id === id) ??
        this.licenseToTransfer;
      if (!lic) {
        continue;
      }
      const key = byLicenseId.get(id);
      if (!key) {
        continue;
      }

      const updated: DesktopLicenseDetails = {
        ...lic,
        unique_id: key.unique_id,
        activation_key: key,
      };
      if (this.isTransfer) {
        updated.transfers_left = lic.transfers_left - 1;
      }
      this.licenseTransferred.emit(updated);
    }

    const n = response.activation_keys.length;
    this.toastService.addToast(
      'success',
      n === 1
        ? 'License transferred successfully'
        : `${n} licenses transferred successfully`
    );
    console.log(
      'license ids transferred',
      response.activation_keys.map((k) => k.desktop_license_id)
    );
    this.closeTransferDialog();
  }

  handleLicenseTransferError(error: unknown) {
    this.transferingLicense = false;
    console.error('error', error);
    const errMessage = this.errorService.getErrorMessage(error);
    this.errorTransferingLicense = errMessage;
    this.toastService.addToast(
      'error',
      'Error transferring license: ' + errMessage
    );
  }

  showRegenerateKeyDialog(license: DesktopLicenseDetails): void {
    this.licenseToRegenerate = license;
    this.errorRegeneratingKey = undefined;
    this.regenerateKeyDialog.nativeElement.showModal();
  }

  closeRegenerateKeyDialog(): void {
    this.regenerateKeyDialog.nativeElement.close();
    this.licenseToRegenerate = null;
    this.regeneratingKey = false;
    this.errorRegeneratingKey = undefined;
  }

  confirmRegenerateKey(): void {
    if (!this.licenseToRegenerate) {
      return;
    }

    const uniqueId = this.licenseCurrentUniqueId(this.licenseToRegenerate);
    if (!uniqueId) {
      this.errorRegeneratingKey = 'License has no Unique ID.';
      return;
    }

    this.regeneratingKey = true;
    this.errorRegeneratingKey = undefined;

    this.licensesService
      .transferLicenses([this.licenseToRegenerate.id], uniqueId, false, false)
      .subscribe({
        next: (response) => {
          this.regeneratingKey = false;
          const key = response.activation_keys[0];
          if (key && this.licenseToRegenerate) {
            this.licenseTransferred.emit({
              ...this.licenseToRegenerate,
              unique_id: key.unique_id ?? this.licenseToRegenerate.unique_id,
              activation_key: key,
            });
          }
          this.toastService.addToast('success', 'Activation key regenerated');
          this.closeRegenerateKeyDialog();
        },
        error: (error: unknown) => {
          this.regeneratingKey = false;
          console.error('error', error);
          const errMessage = this.errorService.getErrorMessage(error);
          this.errorRegeneratingKey = errMessage;
          this.toastService.addToast(
            'error',
            'Error regenerating key: ' + errMessage
          );
        },
      });
  }
}
